'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import { FILTER_TYPE } from '../Filters.types'

import FilterDropdowns from './FilterDropdowns'
import FiltersSheet from './FiltersSheet'

export interface UpdateFiltersProps {
  type: FILTER_TYPE
  value: string
  maxOptionsCount: number
}

interface Props {
  handleRouteChange: (newRoute: string) => void
}

const AdditionalFilters = ({ handleRouteChange }: Props) => {
  const [selectedFilters, setSelectedFilters] = useState<{ [key in FILTER_TYPE]: Array<string> }>({
    [FILTER_TYPE.LEVEL]: [],
    [FILTER_TYPE.LOCATION_OFFERING]: [],
  })

  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams.toString())

  const handleApply = () => {
    Object.keys(selectedFilters).forEach((type) => {
      // Clear current param value
      params.delete(type)
      // Append new values to new url params
      const filterValues = selectedFilters[type as FILTER_TYPE]
      filterValues.forEach((filterValue) => params.append(type, filterValue))
    })
    handleRouteChange(`${window.location.pathname}?${params.toString()}`)
  }

  const updateFilters =
    ({ type, value, maxOptionsCount }: UpdateFiltersProps) =>
    () => {
      const currentSelections = selectedFilters[type]
      const isSelected = currentSelections.includes(value)

      const newFilters = isSelected
        ? currentSelections.filter((option) => option !== value)
        : [...currentSelections, value]

      // If all options are selected, no need for filter params
      if (newFilters.length === maxOptionsCount) {
        setSelectedFilters((prev) => ({ ...prev, [type]: [] }))
      } else {
        setSelectedFilters((prev) => ({ ...prev, [type]: newFilters }))
      }
    }

  const resetFilter = (type: FILTER_TYPE) => () => {
    setSelectedFilters((prev) => ({ ...prev, [type]: [] }))
  }

  const resetAllFilters = () => {
    Object.values(FILTER_TYPE).forEach((filterType) => {
      params.delete(filterType)
    })
    handleRouteChange(`${window.location.pathname}?${params.toString()}`)
  }

  const setInitialFilterSelection = () => {
    setSelectedFilters({
      [FILTER_TYPE.LEVEL]: searchParams.getAll(FILTER_TYPE.LEVEL),
      [FILTER_TYPE.LOCATION_OFFERING]: searchParams.getAll(FILTER_TYPE.LOCATION_OFFERING),
    })
  }

  useEffect(() => {
    setInitialFilterSelection()
  }, [searchParams])

  return (
    <>
      <FiltersSheet
        selectedFilters={selectedFilters}
        updateFilters={updateFilters}
        resetFilter={resetFilter}
        resetAllFilters={resetAllFilters}
        handleApply={handleApply}
        undoFilterSelection={setInitialFilterSelection}
      />
      <FilterDropdowns
        selectedFilters={selectedFilters}
        updateFilters={updateFilters}
        resetFilter={resetFilter}
        handleApply={handleApply}
        undoFilterSelection={setInitialFilterSelection}
      />
    </>
  )
}

export default AdditionalFilters
